import React, { createContext, useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { attachAuthInterceptor } from "../api/apiServices";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token,     setToken]     = useState(() => localStorage.getItem("user_token"));
  const [user,      setUser]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  // true once the background server refresh has completed (or failed).
  // Gates that depend on server-only fields (hasTransactionPin, kycStatus)
  // should wait for this before blocking the user.
  const [userReady, setUserReady] = useState(false);

  const logoutRef = useRef(null);

  const handleLogout = (message = "Logged out successfully") => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("user_token");
    localStorage.removeItem("user");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
    localStorage.removeItem("phone");
    localStorage.removeItem("hasTransactionPin");
    toast.info(message);
  };

  const handleLogin = (token, userData = null) => {
    setToken(token);
    localStorage.setItem("user_token", token);

    try {
      const decoded = jwtDecode(token);
      const mergedUser = {
        ...decoded,
        ...userData,
        userId:         userData?.userId         || decoded?.userId,
        role:           userData?.role           || decoded?.role,
        username:       userData?.userName       || decoded?.userName,
        email:          userData?.email          || decoded?.email,
        phone:          userData?.phone          || decoded?.phone,
        // Currency fields from backend login response (new — multi-country support)
        countryCode:    userData?.countryCode    || decoded?.countryCode    || null,
        currency:       userData?.currency       || decoded?.currency       || "NGN",
        currencySymbol: userData?.currencySymbol || "₦",
      };

      _persistUser(mergedUser);
      setUser(mergedUser);
      return mergedUser;
    } catch (err) {
      console.error("Failed to decode token:", err);
      return null;
    }
  };

  // ── updateUser ────────────────────────────────────────────────
  // Call after any profile update to sync state + localStorage immediately.
  const updateUser = (updatedFields) => {
    setUser(prev => {
      const next = { ...prev, ...updatedFields };
      _persistUser(next);
      return next;
    });
  };

  // ── refreshUser ───────────────────────────────────────────────
  // Fetches the full user profile from the server and merges it
  // into context + localStorage. Solves the cross-device problem:
  // hasTransactionPin, phone, kycStatus etc. are always fresh from
  // the server, not stale from a different device's localStorage.
  const refreshUser = async () => {
    const t = localStorage.getItem("user_token");
    if (!t) return;
    try {
      // Dynamic import to avoid circular deps — apiServices uses AuthContext
      const { getUser } = await import("../api/apiServices");
      const res = await getUser();
      if (res?.status && res.user) {
        setUser(prev => {
          const next = { ...prev, ...res.user };
          _persistUser(next);
          return next;
        });
      }
    } catch (err) {
      // Silent — never block the app for a background refresh failure
      console.warn("[AuthContext] refreshUser failed:", err.message);
    } finally {
      setUserReady(true); // mark ready regardless of success/failure
    }
  };

  // ── Internal helper ───────────────────────────────────────────
  function _persistUser(u) {
    localStorage.setItem("user",              JSON.stringify(u));
    localStorage.setItem("username",          u.username || u.userName || "");
    localStorage.setItem("role",              u.role     || "");
    localStorage.setItem("userId",            u.userId   || "");
    localStorage.setItem("phone",             u.phone    || "");
    localStorage.setItem("hasTransactionPin", String(!!u.hasTransactionPin));
  }

  useEffect(() => {
    logoutRef.current = () => handleLogout("Session expired. Please login again.");
  });

  useEffect(() => {
    attachAuthInterceptor(() => logoutRef.current?.());
  }, []);

  // ── Mount: hydrate from localStorage, then silently refresh from server ──
  useEffect(() => {
    const existingToken = localStorage.getItem("user_token");

    if (!existingToken) {
      setLoading(false);
      setUserReady(true); // no session — gate can render immediately
      return;
    }

    try {
      const decoded = jwtDecode(existingToken);
      if (decoded.exp * 1000 < Date.now()) {
        handleLogout("Session expired. Please login again.");
        setLoading(false);
        return;
      }

      // Step 1 — restore from localStorage immediately so the UI isn't blank
      const stored   = localStorage.getItem("user");
      const hydrated = stored ? JSON.parse(stored) : decoded;

      // Backfill for sessions created before hasTransactionPin was persisted
      if (hydrated.hasTransactionPin === undefined) {
        hydrated.hasTransactionPin =
          localStorage.getItem("hasTransactionPin") === "true";
      }

      setToken(existingToken);
      setUser(hydrated);
      setLoading(false);

      // Step 2 — background refresh from server to get authoritative values.
      // This is what fixes the cross-device scenario:
      // - New device has no localStorage → hydrated.hasTransactionPin = false
      // - refreshUser() fetches from server → gets hasTransactionPin: true
      // - updateUser merges it in silently — no flicker, no re-login needed
      refreshUser();

    } catch {
      handleLogout("Session expired. Please login again.");
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      token,
      user,
      loading,
      setUser,       // raw setter — use updateUser when possible
      updateUser,    // syncs specific fields to state + localStorage
      refreshUser,   // re-fetches full profile from server (cross-device fix)
      userReady,     // true once server refresh is complete — use in gates
      login:  handleLogin,
      logout: handleLogout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
